import React, { useState, useEffect } from 'react';
import { Bot, Search, User, Mail, Phone, MapPin, Calendar, Award, Send, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Avatar } from './ui/avatar';
import { toast } from '../hooks/use-toast';
import { apiService } from '../services/apiService';

const AIAssistedEmployeeLookup = ({ onEmployeeSelected, onClose }) => {
  const [step, setStep] = useState('chat'); // 'chat', 'results', 'details'
  const [chatMessages, setChatMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Start with a welcome message
    setChatMessages([{
      id: 1,
      type: 'ai',
      content: "Hi! I can help you find employee information. You can ask me things like:\n\n• \"Find John Smith\"\n• \"Show me all teachers in the math department\"\n• \"Who works at Lincoln Elementary?\"\n• \"Find employees hired in 2023\"\n\nWhat employee information are you looking for?",
      timestamp: new Date()
    }]);
  }, []);

  const sendMessage = async (message) => {
    if (!message.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: message,
      timestamp: new Date()
    };

    setChatMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    try {
      const response = await apiService.makeRequest('/api/ai/chat', {
        method: 'POST',
        body: JSON.stringify({
          message: message,
          context: { workflow: 'employee_lookup' }
        })
      });

      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: response.response,
        timestamp: new Date(),
        actions: response.actions || []
      };

      setChatMessages(prev => [...prev, aiMessage]);

      // Check if we have employee search results
      if (response.actions && response.actions.some(action => action.type === 'show_employees')) {
        const showAction = response.actions.find(action => action.type === 'show_employees');
        setSearchResults(showAction.data.employees || []);
        setStep('results');
      } else if (response.actions && response.actions.some(action => action.type === 'show_employee')) {
        const showAction = response.actions.find(action => action.type === 'show_employee');
        setSelectedEmployee(showAction.data);
        setStep('details');
      }

    } catch (error) {
      console.error('Failed to send message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: "I'm sorry, I encountered an error searching for employees. Please try again or use the manual search.",
        timestamp: new Date(),
        isError: true
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const loadEmployeeDetails = async (employeeId) => {
    try {
      setIsLoading(true);
      const employee = await apiService.getEmployee(employeeId);
      setSelectedEmployee(employee);
      setStep('details');
    } catch (error) {
      console.error('Failed to load employee details:', error);
      toast({
        title: 'Error',
        description: 'Failed to load employee details.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmployeeSelect = (employee) => {
    if (onEmployeeSelected) {
      onEmployeeSelected(employee);
    } else {
      loadEmployeeDetails(employee.id);
    }
  };

  if (step === 'details' && selectedEmployee) {
    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <User className="w-5 h-5 mr-2 text-yellow-600" />
              Employee Details
            </CardTitle>
            <Button variant="outline" onClick={() => setStep('chat')}>
              New Search
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Employee Profile */}
            <div className="lg:col-span-1">
              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-24 h-24 bg-gradient-to-r from-gray-600 to-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold text-2xl">
                      {(selectedEmployee.first_name || ' ')[0]}{(selectedEmployee.last_name || ' ')[0]}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">
                    {selectedEmployee.first_name} {selectedEmployee.last_name}
                  </h3>
                  <p className="text-gray-600 mb-4">{selectedEmployee.position_title || 'Staff Member'}</p>
                  
                  <div className="space-y-2 text-sm">
                    {selectedEmployee.email && (
                      <div className="flex items-center justify-center text-gray-600">
                        <Mail className="w-4 h-4 mr-2" />
                        {selectedEmployee.email}
                      </div>
                    )}
                    {selectedEmployee.school_name && (
                      <div className="flex items-center justify-center text-gray-600">
                        <MapPin className="w-4 h-4 mr-2" />
                        {selectedEmployee.school_name}
                      </div>
                    )}
                    {selectedEmployee.hire_date && (
                      <div className="flex items-center justify-center text-gray-600">
                        <Calendar className="w-4 h-4 mr-2" />
                        Hired {new Date(selectedEmployee.hire_date).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Employee Information */}
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Basic Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Employee ID</label>
                      <p className="text-gray-900">{selectedEmployee.id}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Department</label>
                      <p className="text-gray-900">{selectedEmployee.department || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">FTE</label>
                      <p className="text-gray-900">{selectedEmployee.fte || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Status</label>
                      <Badge className="bg-green-100 text-green-800">Active</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {selectedEmployee.notes && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700">{selectedEmployee.notes}</p>
                  </CardContent>
                </Card>
              )}

              <div className="flex justify-end space-x-4">
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
                <Button 
                  onClick={() => handleEmployeeSelect(selectedEmployee)}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black"
                >
                  Select Employee
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (step === 'results') {
    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <Search className="w-5 h-5 mr-2 text-yellow-600" />
              Search Results ({searchResults.length} found)
            </CardTitle>
            <Button variant="outline" onClick={() => setStep('chat')}>
              New Search
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {searchResults.length === 0 ? (
            <div className="text-center py-8">
              <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No employees found</h3>
              <p className="text-gray-600">Try refining your search criteria.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {searchResults.map((employee) => (
                <Card 
                  key={employee.id} 
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => handleEmployeeSelect(employee)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-r from-gray-600 to-gray-800 rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold text-sm">
                          {(employee.first_name || ' ')[0]}{(employee.last_name || ' ')[0]}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-gray-900 truncate">
                          {employee.first_name} {employee.last_name}
                        </h4>
                        <p className="text-xs text-gray-500 truncate">
                          {employee.position_title || 'Staff Member'}
                        </p>
                        <p className="text-xs text-gray-400 truncate">
                          {employee.school_name || 'Unassigned'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          
          <div className="mt-6 flex justify-center">
            <Button 
              variant="outline" 
              onClick={() => setStep('chat')}
            >
              Refine Search
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-2xl mx-auto h-96 flex flex-col">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center">
          <Bot className="w-5 h-5 mr-2 text-yellow-600" />
          AI-Assisted Employee Search
        </CardTitle>
      </CardHeader>

      <CardContent className="flex-1 p-0 overflow-hidden">
        <div className="h-full p-4 overflow-y-auto">
          <div className="space-y-4">
            {chatMessages.map((message) => (
              <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                  message.type === 'user' 
                    ? 'bg-yellow-500 text-black' 
                    : message.isError 
                      ? 'bg-red-50 text-red-800 border border-red-200'
                      : 'bg-gray-100 text-gray-900'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  <div className="mt-1">
                    <span className="text-xs opacity-70">
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-gray-100 px-4 py-2 rounded-lg">
                  <div className="flex items-center space-x-1">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                    <span className="text-xs text-gray-500 ml-2">Searching...</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>

      <div className="p-4 border-t">
        <form onSubmit={handleSubmit} className="flex space-x-2">
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Ask me to find an employee..."
            disabled={isTyping}
            className="flex-1"
          />
          <Button 
            type="submit" 
            disabled={isTyping || !inputValue.trim()}
            className="bg-yellow-500 hover:bg-yellow-600 text-black"
          >
            {isTyping ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </form>
      </div>
    </Card>
  );
};

export default AIAssistedEmployeeLookup;